import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { SimpleList } from "components/SimpleList";
import { VerticalSpace } from "components/Spacing";
import {
  useModal,
  ConfirmationModal,
  InfoModal,
} from "components/Modal";

import { apiPost, apiPut, apiDelete, getErrorMessage } from "services/api";
import { usePriceLists } from "shared-apis";

import styles from "./styles.module.scss";

const getCreatePriceListMessage = ({ pricesReady, itemsTotal }) => {
  const total = Number(itemsTotal) || 0;
  if (pricesReady) {
    return `Прайслист успешно создан. Цены для всех товаров (${total}) уже созданы.`;
  }
  return `Прайслист успешно создан. Сейчас постепенно будут создаваться цены для всех товаров (${total}). В ближайшее время лучше сильно не нагружать сервер.`;
};

const PriceListForm = ({ priceList, onSaved }) => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const isEdit = Boolean(priceList?.pricelistid);

  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError("");
    setName(priceList?.name ?? "");
  }, [priceList]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Укажите название");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await apiPut("stores/pricelist", {
          pricelistid: priceList.pricelistid,
          name: name.trim(),
        });
      } else {
        const created = await apiPost("stores/pricelist", {
          name: name.trim(),
        });
        openModal(
          <InfoModal
            title={getCreatePriceListMessage(created ?? {})}
            onClose={closeModal}
            width={480}
          />
        );
      }
      queryClient.invalidateQueries(["pricelists"]);
      onSaved();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      className={styles.rightPanel}
      header={
        <BoldText>{isEdit ? priceList.name : "Новый прайслист"}</BoldText>
      }
      footer={
        <ActionsRow>
          {!!error && (
            <RegularText size={13} color="danger" className={styles.footerError}>
              {error}
            </RegularText>
          )}
          <Button
            icon="galochka"
            variant="secondary"
            onClick={handleSave}
            loading={saving}
          >
            Сохранить
          </Button>
        </ActionsRow>
      }
    >
      <div className={styles.form}>
        {isEdit && (
          <>
            <Input
              label="Код"
              labelWidth={90}
              value={priceList.pricelistid}
              disabled
              inputWidth={100}
            />
            <VerticalSpace size={12} />
          </>
        )}
        <Input
          label="Название"
          labelWidth={90}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          inputWidth={300}
        />
      </div>
    </PageLayout>
  );
};

export const PricesPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const priceListsQuery = usePriceLists();
  const priceLists = priceListsQuery.data?.items ?? [];

  const [activePriceList, setActivePriceList] = useState(null);

  const onDeleteClick = () => {
    if (!activePriceList?.pricelistid) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить прайс лист "${activePriceList.name}"?`}
        onConfirm={async () => {
          try {
            await apiDelete("stores/pricelist", {
              pricelistid: Number(activePriceList.pricelistid),
            });
            setActivePriceList(null);
            queryClient.invalidateQueries(["pricelists"]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  // Копия: клон без id, следующий Save создаст новый прайслист
  const onCopyClick = () => {
    if (!activePriceList?.pricelistid) return;
    setActivePriceList({
      name: `${activePriceList.name}_copy`,
      pricelistid: undefined,
    });
  };

  return (
    <div className={styles.page}>
      <PageLayout
        className={styles.leftPanel}
        header={<BoldText>Прайс листы</BoldText>}
        footer={
          <ActionsRow>
            <Button
              icon="plus"
              onClick={() => setActivePriceList(null)}
              title="Добавить прайслист"
            />
            <Button
              icon="delete"
              onClick={onDeleteClick}
              disabled={!activePriceList?.pricelistid}
              title="Удалить"
            />
            <Button
              icon="copy"
              onClick={onCopyClick}
              disabled={!activePriceList?.pricelistid}
            >
              Копировать
            </Button>
          </ActionsRow>
        }
      >
        <SimpleList
          items={priceLists}
          rowKey="pricelistid"
          activeId={activePriceList?.pricelistid}
          loading={priceListsQuery.isLoading}
          error={
            priceListsQuery.isError
              ? getErrorMessage(priceListsQuery.error)
              : ""
          }
          onRetry={priceListsQuery.refetch}
          onRowClick={setActivePriceList}
        />
      </PageLayout>
      <PriceListForm
        priceList={activePriceList}
        onSaved={() => setActivePriceList(null)}
      />
    </div>
  );
};
